import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Building2, Warehouse, Store, Monitor } from 'lucide-react';
import { businessSettingsAPI } from '@/services/api';

const HQLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ company_name: 'SY-Store', logo_url: '' });

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await businessSettingsAPI.getPublicBranding();
        setBranding(res.data);
      } catch (err) {
        console.error('Failed to fetch branding:', err);
      }
    };
    fetchBranding();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    setLoading(false);

    if (result.success) {
      if (result.user.entity_type === 'hq') {
        navigate('/hq');
      } else {
        setError('This login is for HQ users only. Please use the appropriate login page.');
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white mb-4 shadow-lg shadow-orange-200 overflow-hidden p-2">
              <img src={branding.logo_url} alt={branding.company_name} className="max-w-full max-h-full object-contain" />
            </div>
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 mb-4 shadow-lg shadow-orange-200">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          )}
          <h1 className="text-3xl font-bold text-gray-800">{branding.company_name || 'SY-Store'} HQ</h1>
          <p className="text-gray-500 mt-2">Headquarters Management Portal</p>
        </div>

        <Card className="border-gray-200 bg-white shadow-xl shadow-gray-200/50">
          <CardHeader>
            <CardTitle className="text-gray-800">HQ Sign In</CardTitle>
            <CardDescription className="text-gray-500">
              Access the headquarters management system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4 bg-red-50 border-red-200 text-red-700">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-700">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-gray-50 border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-orange-500 focus:ring-orange-500"
                  required
                  data-testid="username-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-50 border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-orange-500 focus:ring-orange-500"
                  required
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-200"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In to HQ'
                )}
              </Button>
            </form>

            {/* Other Login Links */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center mb-3">Other Portals</p>
              <div className="flex gap-2">
                <Link to="/login/warehouse" className="flex-1">
                  <Button variant="outline" className="w-full border-teal-300 text-teal-600 hover:bg-teal-50">
                    <Warehouse className="w-4 h-4 mr-2" />
                    Warehouse
                  </Button>
                </Link>
                <Link to="/login/store" className="flex-1">
                  <Button variant="outline" className="w-full border-emerald-300 text-emerald-600 hover:bg-emerald-50">
                    <Store className="w-4 h-4 mr-2" />
                    Store POS
                  </Button>
                </Link>
              </div>
              {/* Local Store Mode */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <Link to="/local-store">
                  <Button variant="outline" className="w-full border-gray-400 text-gray-600 hover:bg-gray-100">
                    <Monitor className="w-4 h-4 mr-2" />
                    Local Store Mode (Offline POS)
                  </Button>
                </Link>
                <p className="text-xs text-gray-400 mt-1 text-center">
                  Test offline store with local server on port 8002
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HQLoginPage;
